import clsx from 'clsx';
import { HTMLProps, useContext, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Copy, Share } from 'components/icons';
import { CheckmarkSVG } from 'components/icons/Checkmark';
import { Facebook } from 'components/icons/social/Facebook';
import { LinkedIn } from 'components/icons/social/LinkedIn';
import { X } from 'components/icons/social/X';
import { Button } from 'components/rebrand/Button';
import { LocaleContext } from 'components/rebrand/Layout';

const SocialLink = ({ children, ...linkProps }: HTMLProps<HTMLAnchorElement>) => {
  return (
    <a
      className="flex w-max flex-row items-center gap-8 text-sm font-medium text-white hover:text-primary-100 focus:text-primary-100"
      {...linkProps}
    >
      {children}
    </a>
  );
};

type Props = {
  pathname: string;
  title: string;
};

export const ArticleShare = ({ pathname, title }: Props) => {
  const [isCopied, setIsCopied] = useState(false);
  const locale = useContext(LocaleContext);

  const shareLinkText = (platform: string) => ({ en: `Share on ${platform}`, fr: `Partager sur ${platform}` })[locale];
  const shareLink = `https://www.koho.ca${pathname}`;
  const shareText = `${title} | KOHO`;

  return (
    <div className="relative flex w-full flex-row gap-8">
      <div className="group">
        <Button
          variant="secondary"
          className="mb-4 flex h-48 w-48 items-center justify-center p-8"
          onClick={() => {
            if (window?.navigator) {
              window.navigator.clipboard.writeText(window.location.href);
              setIsCopied(true);
              setTimeout(() => setIsCopied(false), 2000);
            }
          }}
        >
          <Copy className="h-24 w-24" />
        </Button>
        <div
          className={twMerge(
            clsx(
              'absolute mt-8 flex w-fit scale-0 flex-row items-center gap-8 rounded-8 border border-grey-75 bg-grey-500 px-12 py-8 text-xs font-medium text-white transition-all duration-300',
              'after:absolute after:bottom-full after:left-22 after:-ml-5 after:border-[5px] after:border-[color:transparent_transparent_black_transparent] after:content-[""]',
              isCopied && 'scale-100',
            ),
          )}
        >
          <CheckmarkSVG className="h-20 w-20 text-success-200" />
          {{ en: 'Copied to clipboard', fr: 'Copié dans le presse-papiers' }[locale]}
        </div>
      </div>
      <div className="group relative">
        <Button variant="secondary" className="mb-8 flex h-48 w-48 items-center justify-center p-8">
          <Share className="h-24 w-24" />
        </Button>
        <div
          className={twMerge(
            clsx(
              'absolute z-1 hidden rounded-8 border border-grey-75 bg-grey-500 p-12 text-grey-500 group-hover:block',
              'after:absolute after:bottom-full after:left-22 after:-ml-5 after:border-[5px] after:border-[color:transparent_transparent_black_transparent] after:content-[""]',
            ),
          )}
        >
          <div className="flex flex-col gap-12">
            <SocialLink
              aria-label={shareLinkText('Facebook')}
              href={`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`}
            >
              <Facebook className="text-md" />
              {shareLinkText('Facebook')}
            </SocialLink>
            <SocialLink
              aria-label={shareLinkText('X')}
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                `${shareText} @GetKoho`,
              )}&url=${encodeURIComponent(shareLink)}`}
            >
              <X className="text-md" />
              {shareLinkText('X')}
            </SocialLink>
            <SocialLink
              aria-label={shareLinkText('LinkedIn')}
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
                shareLink,
              )}&title=${encodeURIComponent(shareText)}&summary=${encodeURIComponent(
                shareText,
              )}&source=${encodeURIComponent(shareLink)}`}
            >
              <LinkedIn className="text-md" />
              {shareLinkText('LinkedIn')}
            </SocialLink>
          </div>
        </div>
      </div>
    </div>
  );
};
